import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@mui/material';

// Components
import UrlInput from './components/UrlInput';
import AppStepper from './components/Stepper';
import HtmlPreview from './components/HtmlPreview';
import Inspector from './components/Inspector';
import BottomBar from './components/BottomBar';
import StepTwo from './components/StepTwo';

// Hooks
import { useHtmlFetching } from './hooks/useHtmlFetching';
import { useInspector } from './hooks/useInspector';
import { useRecording } from './hooks/useRecording';

// Constants
import { mandatoryItems } from './constants/items';

// Environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const N8N_URL = process.env.REACT_APP_N8N_URL;

function App() {
  const [url, setUrl] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedElements, setSelectedElements] = useState({});
  const [includeIngestion, setIncludeIngestion] = useState(false);
  const [pubDateIncomplete, setPubDateIncomplete] = useState(false);
  const [contentType, setContentType] = useState('HTML');

  // Custom hooks
  const {
    htmlContent,
    fetchSuccess,
    loading,
    fetchError,
    currentPreviewUrl,
    history,
    contentRef,
    shadowRef,
    handleGetHtml,
    handleBack,
    setCurrentPreviewUrl
  } = useHtmlFetching();

  const {
    activeInspectorItem,
    editingPath,
    editingValue,
    editingError,
    aiMagicLoading,
    getInspectorColor,
    setActiveInspectorItem,
    handleInspectorAIMatch,
    handleInspectorAIMatchItem,
    handleEditingPathChange,
    handleEditingValueChange,
    handleEditingPathSave,
    handleEditingPathCancel,
    updateInspectorSelection
  } = useInspector(shadowRef, selectedElements, setSelectedElements, htmlContent);

  // Always call useRecording and destructure all values
  const {
    recording,
    canStop,
    uploading,
    uploadSuccess,
    uploadedUrl,
    recordError,
    videos,
    loadingVideos,
    handleRecordClick
  } = useRecording(url);

  // Load config from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setUrl(id); // Set input field immediately
      // Fetch config from Supabase as before
      fetch(`${SUPABASE_URL}/rest/v1/specs?id=eq.${encodeURIComponent(id)}`, {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data && data[0] && data[0].mapping) {
          setSelectedElements(data[0].mapping);
          setSelectedItems(Object.keys(data[0].mapping));
        }
        if (data && data[0] && data[0].id) {
          setCurrentPreviewUrl(data[0].id);
          handleGetHtml(data[0].id, false);
        }
      });
    }
  }, [SUPABASE_URL, SUPABASE_KEY]);

  // Handle item selection
  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Handle input Get HTML
  const handleInputGetHtml = () => {
    setCurrentPreviewUrl(url); // Always set, even if the same
    handleGetHtml(url, false); // Always fetch
  };

  // Handle inspector selection
  const handleInspectorSelect = (id) => {
    // If item is already selected, deselect it
    if (selectedElements[id]) {
      setSelectedElements(prev => {
        const newElements = { ...prev };
        delete newElements[id];
        return newElements;
      });
      setSelectedItems(prev => prev.filter(item => item !== id));
    } else {
      // If item is not selected, enter inspection mode
      setActiveInspectorItem(id);
    }
  };

  // Handle include ingestion change
  const handleIncludeIngestionChange = (event) => {
    setIncludeIngestion(event.target.checked);
  };

  // Handle navigation
  const handleNext = () => {
    if (activeStep === 0) {
      handleSaveToSupabase(); // Save selections to Supabase
      setActiveStep(1);
    }
  };

  const handleBackStep = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setCurrentPreviewUrl(url); // Ensure preview knows what to show
      handleGetHtml(url, false);
    }
  };

  // Handle copy mapping
  const handleCopyMapping = (mappingData) => {
    navigator.clipboard.writeText(JSON.stringify(mappingData, null, 2));
  };

  // Handle save to Supabase
  const handleSaveToSupabase = async () => {
    try {
      console.log('Saving to Supabase:', {
        id: url,
        mapping: selectedElements
      });
      const response = await fetch(`${SUPABASE_URL}/rest/v1/specs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`,
          'Prefer': 'resolution=merge-duplicates'
        },
        body: JSON.stringify([
          {
            id: url,
            mapping: selectedElements
          }
        ])
      });
      console.log('Supabase response status:', response.status);
      if (response.ok) {
        console.log('Mapping saved successfully');
      } else {
        const errorText = await response.text();
        console.error('Failed to save mapping:', errorText);
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
    }
  };

  // Intercept link clicks inside the rendered HTML
  useEffect(() => {
    const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
    if (!shadowRoot) return;
    
    function handleLinkClick(e) {
      const anchor = e.target.closest('a');
      if (anchor && anchor.getAttribute('href')) {
        const href = anchor.getAttribute('href');
        
        // If inspector is active, prevent navigation
        if (activeInspectorItem) {
          e.preventDefault();
          return;
        }
        
        // Handle different types of links
        if (href.startsWith('http') || href.startsWith('/')) {
          e.preventDefault();
          
          // Resolve relative URLs to absolute URLs
          let absoluteUrl = href.startsWith('/') 
            ? (new URL(currentPreviewUrl)).origin + href 
            : href;
          
          // Check if it's a PDF link
          const isPdfLink = absoluteUrl.toLowerCase().includes('.pdf');
          
          // Check if it's an external link (different domain)
          const currentDomain = new URL(currentPreviewUrl).hostname;
          const linkDomain = new URL(absoluteUrl).hostname;
          const isExternalLink = currentDomain !== linkDomain;
          
          // Open PDFs and external links in new tab
          if (isPdfLink || isExternalLink) {
            window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
          } else {
            // Navigate within the app for internal links
            handleGetHtml(absoluteUrl, true);
          }
        }
      }
    }
    
    shadowRoot.addEventListener('click', handleLinkClick);
    return () => shadowRoot.removeEventListener('click', handleLinkClick);
  }, [htmlContent, currentPreviewUrl, activeInspectorItem, handleGetHtml]);

  useEffect(() => {
    if (activeStep === 0 && url) {
      setCurrentPreviewUrl(url);
      handleGetHtml(url, false);
    }
  }, [activeStep, url]);

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, bgcolor: '#f5f7fa', minHeight: '100vh', pb: 10 }}>
      {/* Section 1: URL Input and Buttons */}
      <UrlInput
        url={url}
        setUrl={setUrl}
        loading={loading}
        fetchSuccess={fetchSuccess}
        fetchError={fetchError}
        uploading={uploading}
        uploadSuccess={uploadSuccess}
        uploadedUrl={uploadedUrl}
        recordError={recordError}
        onGetHtml={handleInputGetHtml}
        onRecord={handleRecordClick}
        recording={recording}
        canStop={canStop}
      />

      {/* Section 2: Stepper */}
      <AppStepper activeStep={activeStep} />

      {/* Section 3: Content and Inspector */}
      {activeStep === 0 ? (
        <Grid container spacing={2} alignItems="stretch" wrap="wrap" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Left: HTML Preview */}
          <Grid item xs={12} md sx={{ minWidth: 0, flexGrow: 1, maxWidth: { xs: '100%', md: 'calc(100% - 350px)' } }}>
            <HtmlPreview
              currentPreviewUrl={currentPreviewUrl}
              loading={loading}
              fetchSuccess={fetchSuccess}
              url={url}
              contentRef={contentRef}
              shadowRef={shadowRef}
              history={history}
              onBack={handleBack}
              htmlContent={htmlContent}
            />
          </Grid>
          
          {/* Right: Inspector */}
          <Grid item xs={12} md="auto" sx={{ width: { xs: '100%', md: '450px' }, flexShrink: 0, mt: { xs: 2, md: 0 } }}>
            <Inspector
              selectedItems={selectedItems}
              selectedElements={selectedElements}
              activeInspectorItem={activeInspectorItem}
              editingPath={editingPath}
              editingValue={editingValue}
              editingError={editingError}
              aiMagicLoading={aiMagicLoading}
              includeIngestion={includeIngestion}
              videos={videos}
              loadingVideos={loadingVideos}
              onSelectItem={handleSelectItem}
              onInspectorSelect={handleInspectorSelect}
              onInspectorAIMatch={() => handleInspectorAIMatch(currentPreviewUrl)}
              onInspectorAIMatchItem={itemId => handleInspectorAIMatchItem(itemId, currentPreviewUrl)}
              onIncludeIngestionChange={handleIncludeIngestionChange}
              onEditingPathChange={handleEditingPathChange}
              onEditingValueChange={handleEditingValueChange}
              onEditingPathSave={handleEditingPathSave}
              onEditingPathCancel={handleEditingPathCancel}
              getInspectorColor={getInspectorColor}
              setActiveInspectorItem={setActiveInspectorItem}
              pubDateIncomplete={pubDateIncomplete}
              onPubDateIncompleteChange={setPubDateIncomplete}
              updateInspectorSelection={updateInspectorSelection}
              htmlContent={htmlContent}
              onContentTypeChange={setContentType}
              contentType={contentType}
            />
          </Grid>
        </Grid>
      ) : (
        <StepTwo 
          selectedElements={selectedElements}
          url={url}
          onCopyMapping={handleCopyMapping}
          pubDateIncomplete={pubDateIncomplete}
          contentType={contentType}
        />
      )}

      {/* Bottom Navigation Bar */}
      <BottomBar
        activeStep={activeStep}
        selectedItems={selectedItems}
        selectedElements={selectedElements}
        mandatoryItems={mandatoryItems}
        onNext={handleNext}
        onBack={handleBackStep}
        pubDateIncomplete={pubDateIncomplete}
      />
    </Box>
  );
}

export default App; 